import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  FileText, Clock, User, Filter, Loader2,
  Shield, Download, Search
} from "lucide-react";

const AuditLogPage = () => {
  const { t } = useTranslation('audit');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/audit-log`, { withCredentials: true });
      setLogs(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const actionColors = {
    template_migration: "#38BDF8",
    file_upload: "#4ADE80",
    file_download: "#FBBF24",
    assessment_generated: "#E8A838",
    default: "#8B95A5",
  };

  const filtered = filter ? logs.filter(l =>
    l.action?.includes(filter) || l.resource_type?.includes(filter) || l.details?.toLowerCase().includes(filter.toLowerCase())
  ) : logs;

  return (
    <QMSLayout title={t('audit:auditsPage.subtitle')}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white" data-testid="audit-log-title">Audit Log</h2>
          <p className="text-[#8B95A5] text-sm mt-1">Complete audit trail of all system actions</p>
        </div>
        <div className="flex items-center gap-2 bg-[#1F222D] rounded px-3 py-2">
          <Search size={14} className="text-[#8B95A5]" />
          <input type="text" placeholder="Filter events..." value={filter}
            onChange={e => setFilter(e.target.value)}
            className="bg-transparent text-white text-xs outline-none w-40" data-testid="audit-log-filter" />
        </div>
      </div>

      <div className="qms-card">
        <div className="qms-card-body">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-[#8B95A5] py-12">
              <Shield size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No audit events recorded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((log, i) => (
                <div key={log.event_id || i} className="flex items-center gap-4 px-4 py-3 bg-[#111318] rounded hover:bg-[#181B23] transition-colors"
                  data-testid={`audit-event-${i}`}>
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: actionColors[log.action] || actionColors.default }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-xs font-medium">{log.action?.replace(/_/g, " ").toUpperCase()}</span>
                      <span className="text-[#36A9E1] text-[10px]">{log.resource_type}</span>
                    </div>
                    <p className="text-[#8B95A5] text-[11px] truncate">{log.details || `Resource: ${log.resource_id}`}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[#8B95A5] text-[10px]">{log.user_id}</p>
                    <p className="text-[#64748B] text-[10px]">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </QMSLayout>
  );
};

export default AuditLogPage;
